import { buildNewApprovalSetupAttributes } from './newApprovalSetupSchema';

import type { FileWithPath } from 'react-dropzone';

import type { NewApprovalSetupValues } from './newApprovalSetupSchema';
import type {
  IApplicantQuestion,
  IFieldChoice
} from '../../Components/Utilities/QuestionFieldGenerator';
import type { IAnswer, IApprovalForm, IApprovalTemplate, IAvailabilityTableData } from '../types';

export interface IApproverRow {
  email: string;
  name?: string;
  id?: number;
  destroyed?: boolean;
  error?: string;
  mandatoryBlank?: boolean;
}

// Answer state keyed by question id; `data` is whatever shape that question type's field emits.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ApprovalFormQuestions = Record<string, IApplicantQuestion & { data: any }>;

export const createApprovalFormQuestionsObject = (
  questions: IApplicantQuestion[]
): ApprovalFormQuestions => {
  const newObject: ApprovalFormQuestions = {};
  questions.forEach((question: IApplicantQuestion) => {
    if (question.type === 'RankField') {
      newObject[question.id] = {
        ...question,
        data: {}
      };
      question.field_choices.forEach((choice: IFieldChoice, index: number) => {
        newObject[question.id].data[choice.id] = index + 1;
      });
    } else if (question.type === 'JobReferenceLookupField') {
      newObject[question.id] = {
        ...question,
        data: 'validation bypass'
      };
    } else {
      newObject[question.id] = {
        ...question,
        data: ''
      };
    }
  });
  return newObject;
};

export const createQuestionErrorsObject = (
  questions: IApplicantQuestion[]
): Record<number, string> => {
  const errors: Record<number, string> = {};
  questions.forEach((question: IApplicantQuestion) => {
    errors[question.id] = '';
  });
  return errors;
};

export const containsDuplicates = (array: string[]): boolean =>
  new Set(array).size !== array.length;

export const normalizeAvailabilityAnswerOffsets = (
  answer: Record<string, IAvailabilityTableData>
): Record<string, IAvailabilityTableData> => {
  const hasZeroBasedOffsets = Object.keys(answer).some((key) => Number(key) === 0);

  if (!hasZeroBasedOffsets) return { ...answer };

  return Object.fromEntries(
    Object.entries(answer).map(([key, value]) => {
      const numericKey = Number(key);
      return [Number.isNaN(numericKey) ? key : String(numericKey + 1), value];
    })
  );
};

export const getAnswerAttributes = (
  fieldId: string | number,
  answerValue: { data: unknown; id: number; type: string },
  answers?: IAnswer[]
): Record<string, unknown> => {
  const questionObj = answers?.find((answer: IAnswer) => answer.field_id === answerValue.id);
  if (answerValue.type === 'RadioButtonField' || answerValue.type === 'SelectField') {
    return {
      field_id: fieldId,
      field_choice_id: answerValue.data,
      id: questionObj?.id
    };
  }
  if (answerValue.type === 'CheckBoxField') {
    return {
      field_id: fieldId,
      field_choice_ids: Object.entries(answerValue.data as Record<string, unknown>)
        .filter(([, choiceValue]) => choiceValue)
        .map(([choiceId]) => choiceId),
      id: questionObj?.id
    };
  }
  return {
    field_id: fieldId,
    data: answerValue.data,
    id: questionObj?.id
  };
};

// The key of each entry is its index in the position-sorted list of *all* questions, so editing an
// approval (which skips unanswered questions) yields sparse keys. The server receives them as-is.
export const buildAnswersAttributes = (
  approvalFormQuestions: ApprovalFormQuestions,
  answers: IAnswer[] | undefined,
  answeredIds: number[] | null,
  isEditing: boolean
): Record<number, Record<string, unknown>> =>
  Object.fromEntries(
    Object.entries(approvalFormQuestions)
      .sort((a, b) => a[1].position - b[1].position)
      .map(([id, value], index) => ({ id, value, index }))
      .filter(({ value }) => !isEditing || answeredIds?.includes(value.id))
      .map(({ id, value, index }) => [index, getAnswerAttributes(id, value, answers)])
  );

export const buildApproversAttributes = (
  approvers: IApproverRow[]
): Record<number, Record<string, unknown>> =>
  Object.fromEntries(
    approvers.map((approver, index) => [
      index,
      {
        id: approver.id,
        email: approver.email.includes('*') ? approver.email.slice(1) : approver.email,
        position: 0,
        required: approver.email.includes('*'),
        _destroy: approver.destroyed
      }
    ])
  );

export const buildDocumentsAttributes = (
  fileList: FileWithPath[]
): Record<number, { attached: FileWithPath }> =>
  Object.fromEntries(fileList.map((document, index) => [index, { attached: document }]));

export const buildApprovalPayload = ({
  answersAttributes,
  approversAttributes,
  cognologyRecordId,
  documentsAttributes,
  newUI,
  setupValues,
  templateId,
  templateIdFromParams
}: {
  answersAttributes: Record<string, unknown>;
  approversAttributes: Record<string, unknown>;
  cognologyRecordId: string | null;
  documentsAttributes: Record<string, unknown>;
  newUI: string | null;
  setupValues: NewApprovalSetupValues;
  templateId?: number;
  templateIdFromParams: string | null;
}): { requisition: Record<string, unknown>; new_ui: true | undefined } => ({
  requisition: {
    requisition_form_id:
      templateIdFromParams === '' ? templateId : (templateIdFromParams ?? templateId),
    cognology_record_id: cognologyRecordId,
    documents_attributes: documentsAttributes,
    approvers_attributes: approversAttributes,
    ...buildNewApprovalSetupAttributes(setupValues),
    answers_attributes: answersAttributes
  },
  new_ui: newUI ? true : undefined
});

// Approvers pre-filled on a new approval: the template's own defaults win over the entity's.
export const buildDefaultApprovers = (
  approvalTemplateData: IApprovalTemplate
): IApproverRow[] | null => {
  if (approvalTemplateData.config.default_approvers) {
    return approvalTemplateData.config.default_approvers.split(',').map((approver) => ({
      email: approver,
      destroyed: false,
      mandatoryBlank: approver === '*'
    }));
  }
  if (approvalTemplateData.entity_default_approver) {
    return approvalTemplateData.entity_default_approver.map((approver) => ({
      email: approver.required ? `*${approver.email}` : approver.email,
      destroyed: false,
      mandatoryBlank: approver.required
    }));
  }
  return null;
};

export const buildExistingApprovers = (
  approvalFormData: IApprovalForm,
  approvalTemplateData: IApprovalTemplate
): IApproverRow[] =>
  approvalFormData.approvers?.map((approver, index) => ({
    email: approver.email,
    id: approver.id,
    destroyed: false,
    name: approver.name,
    mandatoryBlank: approvalTemplateData?.config?.default_approvers?.split(',')?.at(index) === '*'
  })) ?? [];

const textAnswer = (answer: IAnswer): unknown => answer.answer;

// How each saved answer type maps back to the value its field component works with.
const ANSWER_TO_QUESTION_DATA = new Map<string, (answer: IAnswer) => unknown>([
  ['TextField', textAnswer],
  ['TextAreaField', textAnswer],
  ['CsvDataLookupField', textAnswer],
  ['DateField', textAnswer],
  ['SelectField', (answer) => answer.field_choice_ids.at(0)],
  ['RadioButtonField', (answer) => answer.field_choice_ids.at(0)],
  [
    'RankField',
    (answer) =>
      Object.fromEntries(answer.field_choice_ids.map((id, index) => [id, (index + 1).toString()]))
  ],
  [
    'CheckBoxField',
    (answer) => Object.fromEntries(answer.field_choice_ids.map((id) => [id, true]))
  ],
  [
    'AvailabilityField',
    (answer) =>
      normalizeAvailabilityAnswerOffsets(answer.answer as Record<string, IAvailabilityTableData>)
  ]
]);

// Overlays a saved approval's answers onto the blank question object built from the template.
// Answer types missing from ANSWER_TO_QUESTION_DATA leave the question's blank default untouched.
export const hydrateQuestionAnswers = (
  questionObj: ApprovalFormQuestions,
  answers: IAnswer[] | undefined
): ApprovalFormQuestions => {
  const entries = new Map(Object.entries(questionObj));
  answers?.forEach((answer) => {
    const toData = ANSWER_TO_QUESTION_DATA.get(answer.type);
    const found = [...entries].find(([, question]) => question.id === answer.field_id);
    if (!toData || !found) return;

    const [questionId, question] = found;
    entries.set(questionId, { ...question, data: toData(answer) });
  });
  return Object.fromEntries(entries);
};
